import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Close,
  Delete,
  ExpandLess,
  ExpandMore,
  RestoreFromTrash,
} from "@material-ui/icons";
import {
  DatePicker,
  LoadingButton,
  LocalizationProvider,
} from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { tiposApi } from "src/services/tiposApi";
import { formatValorItem } from "src/utils/propuestasV2/formatValorItem";
import { claveEmpresaFase } from "src/utils/propuestaItemLabels";
import { derivarDecisionesPorItem } from "src/utils/propuestasV2/decisionesRenegociacionV2";
import { buildAjusteServiciosBody } from "src/utils/propuestasV2/buildAjusteServiciosBody";
import { calcValorConDescuento } from "src/utils/propuestasV2/calcValorConDescuento";
import { BRAND } from "src/theme/brandTokens";
import { strToDate, dateToStr } from "src/utils/propuestasV2/fechasIso";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const MONEDAS = ["CLP", "UF", "USD", "EUR"];

// Únicos valores que acepta el endpoint para la frecuencia de un PLAN --
// cualquier otro string se rechaza. Con eñe: "AÑO", no "ANIO".
const FRECUENCIAS_PLAN = ["MES", "AÑO"];

// Únicos campos numéricos del ítem: viajan al body como Number, nunca como el
// string crudo que deja un input. Un servicio nuevo o el pass-through de los
// campos requeridos pasan igual por esta lista antes de guardar.
const CAMPOS_NUMERICOS = new Set([
  "valorOriginal",
  "descuento",
  "valorConDescuento",
]);

const NUEVO_SERVICIO_INICIAL = {
  tipoItem: "TAREA",
  refCliente: "",
  nombreServicio: "",
  tipoMoneda: "CLP",
  valorOriginal: "",
  descuento: "",
  refTipoPlan: "",
  tipoFrecuencia: "",
  fechaSiguienteCiclo: "",
  fechaTerminoDesc: "",
  fechaLimite: "",
  faseTitulo: "",
};

// ── Helpers ─────────────────────────────────────────────────────────────────
// El backend guarda y espera estos campos como cadena ISO ("yyyy-MM-dd"); el
// DatePicker de @material-ui/lab trabaja con objetos Date. Sin esta conversión
// en las dos direcciones, el valor que llega al servidor deja de ser el
// formato que el resto de los ítems ya tiene guardado.

const getNombreItem = (item) =>
  item?.faseTitulo || item?.nombreServicio || `Servicio #${item?.id}`;

// Convierte a Number las claves numéricas de un objeto de cambios sueltos.
// Un campo numérico dejado en blanco o con un valor que no es un número no se
// convierte a 0 ni se descarta acá -- eso ya se validó antes de llegar a esta
// función -- así que siempre recibe un valor numérico utilizable.
function convertirCambiosANumero(cambios) {
  const salida = {};
  for (const [campo, valor] of Object.entries(cambios)) {
    salida[campo] = CAMPOS_NUMERICOS.has(campo) ? Number(valor) : valor;
  }
  return salida;
}

/**
 * Ajuste de servicios de una propuesta en EN_RENEGOCIACION: la pieza que
 * consume `editarItemsRenegociacionV2`. Edita en el sitio (parche, no
 * reemplazo) -- ver `buildAjusteServiciosBody` para la construcción del
 * cuerpo -- retira marcando (nunca borra) y registra en la bitácora los
 * servicios que el cliente pidió y todavía no existen como ítem.
 *
 * Un servicio que el cliente ya aceptó no se edita ni se retira: se muestra
 * bloqueado, igual que en ModalObservacionInternaV2 (misma fuente de verdad,
 * `item.respuesta` del detalle) y nunca entra a `cambiosPorItem`.
 */
export default function ModalAjusteServiciosV2({
  open,
  setOpen,
  propuesta,
  onSuccess,
  onAjusteGuardado,
  maxWidth = "lg",
  fullWidth = true,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [editarItems, { isLoading: guardando }] =
    propuestasApi.useEditarItemsRenegociacionV2Mutation();

  const { data: catalogoPlanes = [] } = tiposApi.useGetTipoPlanesQuery(
    undefined,
    { skip: !open },
  );
  // Mismo filtro que usa el selector de plan del editor de borrador: el
  // endpoint no valida que el plan elegido siga activo, así que ofrecer uno
  // dado de baja lo dejaría vigente en un servicio nuevo sin que nadie lo
  // note hasta que alguien intente facturarlo.
  const planesActivos = useMemo(
    () => catalogoPlanes.filter((p) => p.activo === 1 || p.activo === true),
    [catalogoPlanes],
  );

  const items = useMemo(
    () => (Array.isArray(propuesta?.items) ? propuesta.items : []),
    [propuesta],
  );

  // La decisión vigente de cada ítem viaja en `item.respuesta` (detalle de la
  // propuesta), no se deriva del hilo: es la misma regla que ya usan
  // ModalObservacionInternaV2 y ModalCierreExplicitoV2.
  const decisionesPorItem = useMemo(
    () => derivarDecisionesPorItem(items),
    [items],
  );
  const itemsBloqueados = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) === "ACEPTADO"),
    [items, decisionesPorItem],
  );
  const itemsEditables = useMemo(
    () => items.filter((item) => decisionesPorItem.get(item.id) !== "ACEPTADO"),
    [items, decisionesPorItem],
  );
  const idsAceptados = useMemo(
    () => itemsBloqueados.map((item) => item.id),
    [itemsBloqueados],
  );

  // Agrupar por empresa usa la misma clave que el resto del sistema: el RUT,
  // no la razón social. Dos sociedades con el mismo nombre no pueden caer en
  // el mismo grupo, o sus subtotales se sumarían en un bloque único.
  const gruposEditables = useMemo(() => {
    const mapa = new Map();
    for (const item of itemsEditables) {
      const key = claveEmpresaFase(item) || "__sin_empresa__";
      if (!mapa.has(key)) {
        mapa.set(key, { key, nombre: item.cliente?.nombre || key, items: [] });
      }
      mapa.get(key).items.push(item);
    }
    return Array.from(mapa.values());
  }, [itemsEditables]);
  const multiEmpresa = gruposEditables.length > 1;

  const empresasDisponibles = useMemo(() => {
    const mapa = new Map();
    for (const item of items) {
      if (item.refCliente && !mapa.has(item.refCliente)) {
        mapa.set(item.refCliente, item.cliente?.nombre || item.refCliente);
      }
    }
    return Array.from(mapa.entries()).map(([rut, nombre]) => ({
      rut,
      nombre,
    }));
  }, [items]);

  const [cambiosPorItem, setCambiosPorItem] = useState({});
  const [idsARetirar, setIdsARetirar] = useState([]);
  const [expandidoId, setExpandidoId] = useState(null);
  const [itemsNuevos, setItemsNuevos] = useState([]);
  const [mostrarFormNuevo, setMostrarFormNuevo] = useState(false);
  const [nuevoServicio, setNuevoServicio] = useState(NUEVO_SERVICIO_INICIAL);
  const [serviciosSolicitados, setServiciosSolicitados] = useState([]);
  const [procesando, setProcesando] = useState(false);

  const resetearFormularioLocal = () => {
    setCambiosPorItem({});
    setIdsARetirar([]);
    setExpandidoId(null);
    setItemsNuevos([]);
    setMostrarFormNuevo(false);
    setNuevoServicio(NUEVO_SERVICIO_INICIAL);
    setServiciosSolicitados([]);
  };

  const handleClose = () => {
    if (procesando) return;
    resetearFormularioLocal();
    setOpen(false);
  };

  const setCambio = (itemId, campo, valor) => {
    setCambiosPorItem((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [campo]: valor },
    }));
  };

  const toggleRetirar = (itemId) => {
    setIdsARetirar((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
    // Un ítem que se retira no se edita en la misma pasada: los cambios
    // sueltos que tenía cargados se descartan para no dejar en pantalla la
    // apariencia de "se edita y se retira a la vez".
    setCambiosPorItem((prev) => {
      if (!prev[itemId]) return prev;
      const { [itemId]: _descartado, ...resto } = prev;
      return resto;
    });
  };

  const valorEfectivo = (item, campo) => {
    const cambios = cambiosPorItem[item.id];
    if (cambios && Object.prototype.hasOwnProperty.call(cambios, campo)) {
      return cambios[campo];
    }
    return item[campo] ?? "";
  };

  // El precio final que se va a guardar si el vendedor confirma ahora mismo:
  // nunca el que trae `item.valorConDescuento` sin más, porque ese queda
  // vencido apenas se toca el original o el descuento.
  const valorConDescuentoEfectivo = (item) =>
    calcValorConDescuento(
      valorEfectivo(item, "valorOriginal"),
      valorEfectivo(item, "descuento"),
    );

  const agregarLineaSolicitud = () => {
    setServiciosSolicitados((prev) => [...prev, ""]);
  };
  const actualizarLineaSolicitud = (index, valor) => {
    setServiciosSolicitados((prev) =>
      prev.map((s, i) => (i === index ? valor : s)),
    );
  };
  const quitarLineaSolicitud = (index) => {
    setServiciosSolicitados((prev) => prev.filter((_, i) => i !== index));
  };

  const actualizarNuevoServicio = (campo, valor) => {
    setNuevoServicio((prev) => ({ ...prev, [campo]: valor }));
  };

  const validarNuevoServicio = () => {
    if (
      !nuevoServicio.refCliente ||
      !nuevoServicio.nombreServicio.trim() ||
      !nuevoServicio.tipoMoneda ||
      nuevoServicio.valorOriginal === "" ||
      !Number.isFinite(Number(nuevoServicio.valorOriginal))
    ) {
      enqueueSnackbar(
        "Completa empresa, nombre, moneda y el valor original del servicio nuevo.",
        { variant: "error" },
      );
      return false;
    }
    if (nuevoServicio.tipoItem === "PLAN") {
      if (!nuevoServicio.refTipoPlan) {
        enqueueSnackbar(
          "Un servicio nuevo de tipo PLAN necesita un plan del catálogo.",
          { variant: "error" },
        );
        return false;
      }
      // Sin frecuencia, el servicio se activaría más adelante como mensual
      // por defecto, sin que nadie lo haya decidido así: si el plan real es
      // anual, el fallback lo cambia de recurrencia en silencio.
      if (!nuevoServicio.tipoFrecuencia) {
        enqueueSnackbar(
          "Un servicio nuevo de tipo PLAN necesita su frecuencia (mensual o anual).",
          { variant: "error" },
        );
        return false;
      }
      // Sin fecha de inicio, el servicio se activa con ese campo vacio, y es
      // el campo del que sale el calculo de los ciclos siguientes: dejarlo en
      // blanco traslada el problema a la generacion de tareas del plan, donde
      // ya nadie lo relaciona con esta pantalla.
      if (!nuevoServicio.fechaSiguienteCiclo) {
        enqueueSnackbar(
          "Un servicio nuevo de tipo PLAN necesita la fecha de su primer ciclo.",
          { variant: "error" },
        );
        return false;
      }
    }
    return true;
  };

  const agregarNuevoServicio = () => {
    if (!validarNuevoServicio()) return;
    const {
      refTipoPlan,
      tipoFrecuencia,
      fechaSiguienteCiclo,
      fechaTerminoDesc,
      fechaLimite,
      ...resto
    } = nuevoServicio;
    const item = convertirCambiosANumero(resto);
    item.valorConDescuento = calcValorConDescuento(
      nuevoServicio.valorOriginal,
      nuevoServicio.descuento,
    );
    if (nuevoServicio.tipoItem === "PLAN") {
      item.refTipoPlan = refTipoPlan;
      item.tipoFrecuencia = tipoFrecuencia;
      item.fechaSiguienteCiclo = fechaSiguienteCiclo;
    }
    // Fecha límite solo existe en el catálogo de TAREA -- mandarla en un
    // PLAN escribiría una columna que ese servicio nunca usa.
    if (nuevoServicio.tipoItem === "TAREA" && fechaLimite) {
      item.fechaLimite = fechaLimite;
    }
    // Igual que la fecha límite: solo viaja si el vendedor de verdad la fijó,
    // y solo tiene sentido si hay un descuento que vencer.
    if (Number(nuevoServicio.descuento) > 0 && fechaTerminoDesc) {
      item.fechaTerminoDesc = fechaTerminoDesc;
    }
    setItemsNuevos((prev) => [...prev, item]);
    setNuevoServicio(NUEVO_SERVICIO_INICIAL);
    setMostrarFormNuevo(false);
  };

  const quitarNuevoServicio = (index) => {
    setItemsNuevos((prev) => prev.filter((_, i) => i !== index));
  };

  const hayCambiosPendientes =
    Object.values(cambiosPorItem).some(
      (cambios) => cambios && Object.keys(cambios).length > 0,
    ) ||
    idsARetirar.length > 0 ||
    itemsNuevos.length > 0 ||
    serviciosSolicitados.some((s) => s.trim().length > 0);

  const handleGuardar = async () => {
    // Un campo numérico dejado en blanco, o con un texto que no representa un
    // número, no es una orden de vaciarlo -- el schema no admite vacío ni
    // null en esos campos y termina convirtiendo un valor no numérico en
    // cero -- así que se bloquea acá en vez de mandarlo y dejar que el precio
    // se vuelva cero en silencio.
    for (const [idStr, cambios] of Object.entries(cambiosPorItem)) {
      for (const [campo, valor] of Object.entries(cambios ?? {})) {
        if (
          CAMPOS_NUMERICOS.has(campo) &&
          (valor === "" || !Number.isFinite(Number(valor)))
        ) {
          const item = items.find((it) => it.id === Number(idStr));
          enqueueSnackbar(
            `${getNombreItem(item)}: el valor no puede quedar vacío.`,
            { variant: "error" },
          );
          return;
        }
      }
    }

    // El precio final nunca sale de lo que el vendedor escribió en ese
    // campo -- no hay ningún campo para eso -- sino de recalcularlo con la
    // misma fórmula que usa la creación de una propuesta, sobre el valor
    // original y el descuento efectivos (el que cambió, o si no el vigente).
    // Así los tres números del precio no pueden quedar sin relación entre
    // sí en el documento que el cliente firma.
    const cambiosConNumeros = {};
    for (const [idStr, cambios] of Object.entries(cambiosPorItem)) {
      const limpio = convertirCambiosANumero(cambios ?? {});
      if ("valorOriginal" in limpio || "descuento" in limpio) {
        const original = items.find((it) => it.id === Number(idStr));
        const valorOriginalEfectivo = Object.prototype.hasOwnProperty.call(
          limpio,
          "valorOriginal",
        )
          ? limpio.valorOriginal
          : original?.valorOriginal;
        const descuentoEfectivo = Object.prototype.hasOwnProperty.call(
          limpio,
          "descuento",
        )
          ? limpio.descuento
          : original?.descuento;
        limpio.valorConDescuento = calcValorConDescuento(
          valorOriginalEfectivo,
          descuentoEfectivo,
        );
      }
      cambiosConNumeros[idStr] = limpio;
    }

    const body = buildAjusteServiciosBody({
      itemsVigentes: items,
      cambiosPorItem: cambiosConNumeros,
      idsARetirar,
      itemsNuevos,
      serviciosSolicitados: serviciosSolicitados.map((descripcion) => ({
        descripcion,
      })),
      idsAceptados,
    });

    if (
      body.items.length === 0 &&
      body.idsARetirar.length === 0 &&
      body.serviciosSolicitados.length === 0
    ) {
      enqueueSnackbar("No hay ajustes para guardar.", { variant: "info" });
      return;
    }

    setProcesando(true);
    try {
      const resultado = await editarItems({
        id: propuesta.id,
        body,
      }).unwrap();
      enqueueSnackbar(
        `Se registraron ${resultado.cambiosRegistrados} cambios, ${resultado.cantidadRetirados} retiros y ${resultado.cantidadAgregados} servicios nuevos.`,
        { variant: "success" },
      );
      resetearFormularioLocal();
      onAjusteGuardado?.(resultado);
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ||
          error?.message ||
          "Ocurrió un error al guardar los ajustes.",
        { variant: "error" },
      );
    } finally {
      setProcesando(false);
    }
  };

  const renderFilaItem = (item) => {
    const marcadoParaRetirar = idsARetirar.includes(item.id);
    const expandido = expandidoId === item.id;
    // La fecha de término del descuento solo tiene sentido si hay un
    // descuento vigente que vencer -- igual que en el formulario de creación.
    const tieneDescuento = Number(valorEfectivo(item, "descuento")) > 0;
    return (
      <Paper
        key={item.id}
        variant="outlined"
        sx={{
          p: 2,
          mb: 1.5,
          opacity: marcadoParaRetirar ? 0.6 : 1,
          borderColor: marcadoParaRetirar ? "error.light" : "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={item.tipoItem}
                size="small"
                sx={{
                  bgcolor: item.tipoItem === "PLAN" ? "#E3F2FD" : "#FFF3E0",
                  color:
                    item.tipoItem === "PLAN" ? "#1565C0" : BRAND.color.primary,
                  fontWeight: 600,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textDecoration: marcadoParaRetirar ? "line-through" : "none",
                }}
              >
                {valorEfectivo(item, "faseTitulo") ||
                  valorEfectivo(item, "nombreServicio")}
              </Typography>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              {formatValorItem(
                item.tipoMoneda,
                valorConDescuentoEfectivo(item),
              )}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip
              title={
                marcadoParaRetirar
                  ? "Deshacer el retiro"
                  : "Retirar de la propuesta vigente"
              }
            >
              <IconButton
                size="small"
                color={marcadoParaRetirar ? "primary" : "error"}
                onClick={() => toggleRetirar(item.id)}
                disabled={procesando}
              >
                {marcadoParaRetirar ? (
                  <RestoreFromTrash fontSize="small" />
                ) : (
                  <Delete fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {!marcadoParaRetirar && (
              <IconButton
                size="small"
                onClick={() =>
                  setExpandidoId((prev) => (prev === item.id ? null : item.id))
                }
              >
                {expandido ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </IconButton>
            )}
          </Stack>
        </Stack>

        {expandido && !marcadoParaRetirar && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {(item.refTipoPlan || item.refTipoTarea) && (
                <Grid item xs={12}>
                  {/* Solo lectura: lo que se materializa al aceptar depende de
                      esta referencia de catálogo, no del nombre visible --
                      que el campo de abajo sí deja renombrar libremente. */}
                  <Typography variant="caption" color="textSecondary">
                    {item.refTipoPlan
                      ? `Plan del catálogo: ${item.refTipoPlan}`
                      : `Tarea del catálogo: ${item.refTipoTarea}`}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre del servicio"
                  value={valorEfectivo(item, "nombreServicio")}
                  onChange={(e) =>
                    setCambio(item.id, "nombreServicio", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Título en el documento"
                  value={valorEfectivo(item, "faseTitulo")}
                  onChange={(e) =>
                    setCambio(item.id, "faseTitulo", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  label="Valor original"
                  value={valorEfectivo(item, "valorOriginal")}
                  onChange={(e) =>
                    setCambio(item.id, "valorOriginal", e.target.value)
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {item.tipoMoneda}
                      </InputAdornment>
                    ),
                  }}
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  label="Descuento (%)"
                  value={valorEfectivo(item, "descuento")}
                  onChange={(e) =>
                    setCambio(item.id, "descuento", e.target.value)
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor final"
                  value={formatValorItem(
                    item.tipoMoneda,
                    valorConDescuentoEfectivo(item),
                  )}
                  InputProps={{ readOnly: true }}
                  helperText="Calculado automáticamente"
                  sx={{ "& .MuiOutlinedInput-root": { bgcolor: "grey.50" } }}
                />
              </Grid>
              {item.tipoItem === "PLAN" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Frecuencia</InputLabel>
                      <Select
                        label="Frecuencia"
                        value={valorEfectivo(item, "tipoFrecuencia") || ""}
                        onChange={(e) =>
                          setCambio(item.id, "tipoFrecuencia", e.target.value)
                        }
                        disabled={procesando}
                      >
                        {FRECUENCIAS_PLAN.map((frecuencia) => (
                          <MenuItem key={frecuencia} value={frecuencia}>
                            {frecuencia === "MES" ? "Mensual" : "Anual"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha del siguiente ciclo"
                      value={strToDate(
                        valorEfectivo(item, "fechaSiguienteCiclo"),
                      )}
                      onChange={(date) =>
                        setCambio(
                          item.id,
                          "fechaSiguienteCiclo",
                          // "" es una fecha inválida para el schema de este
                          // endpoint: la única forma de vaciar el campo es
                          // mandar null.
                          dateToStr(date) || null,
                        )
                      }
                      disabled={procesando}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </Grid>
                </>
              )}
              {item.tipoItem === "TAREA" && (
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Fecha límite"
                    value={strToDate(valorEfectivo(item, "fechaLimite"))}
                    onChange={(date) =>
                      setCambio(
                        item.id,
                        "fechaLimite",
                        // "" es una fecha inválida para el schema de este
                        // endpoint: la única forma de vaciar el campo es
                        // mandar null.
                        dateToStr(date) || null,
                      )
                    }
                    disabled={procesando}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </Grid>
              )}
              {/* Solo tiene sentido pactar un vencimiento para un descuento
                  que existe -- sin descuento vigente no hay nada que vencer. */}
              {tieneDescuento && (
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Fecha término descuento"
                    value={strToDate(valorEfectivo(item, "fechaTerminoDesc"))}
                    onChange={(date) =>
                      setCambio(
                        item.id,
                        "fechaTerminoDesc",
                        // "" es una fecha inválida para el schema de este
                        // endpoint: la única forma de vaciar el campo es
                        // mandar null.
                        dateToStr(date) || null,
                      )
                    }
                    disabled={procesando}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Objetivo"
                  value={valorEfectivo(item, "faseObjetivo")}
                  onChange={(e) =>
                    setCambio(item.id, "faseObjetivo", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Actividades"
                  value={valorEfectivo(item, "faseActividades")}
                  onChange={(e) =>
                    setCambio(item.id, "faseActividades", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Entregable"
                  value={valorEfectivo(item, "faseEntregable")}
                  onChange={(e) =>
                    setCambio(item.id, "faseEntregable", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Duración"
                  value={valorEfectivo(item, "faseDuracion")}
                  onChange={(e) =>
                    setCambio(item.id, "faseDuracion", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Observaciones"
                  value={valorEfectivo(item, "observaciones")}
                  onChange={(e) =>
                    setCambio(item.id, "observaciones", e.target.value)
                  }
                  disabled={procesando}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Dialog
        open={open}
        maxWidth={maxWidth}
        onClose={handleClose}
        fullWidth={fullWidth}
      >
        <DialogTitle className={classes.dialogTitle}>
          Ajustar servicios — Propuesta #{propuesta?.id}
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            disabled={procesando}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Cada guardado escribe solo lo que cambiaste: un servicio que no
              tocas conserva su texto y sus valores tal como están. Retirar saca
              el servicio de la propuesta vigente, pero lo conserva en el
              historial de la negociación.
            </Typography>
          </Alert>

          {itemsBloqueados.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Ya aceptados por el cliente (no editables)
              </Typography>
              <Stack spacing={1}>
                {itemsBloqueados.map((item) => (
                  <Stack
                    key={item.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getNombreItem(item)}
                    </Typography>
                    <Chip label="Aceptado" size="small" color="success" />
                  </Stack>
                ))}
              </Stack>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ mt: itemsBloqueados.length ? 2 : 0 }}
          >
            Servicios vigentes
          </Typography>
          {itemsEditables.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No quedan servicios editables en esta propuesta.
            </Alert>
          ) : multiEmpresa ? (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {gruposEditables.map((grupo) => (
                <Box key={grupo.key}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: 700 }}
                  >
                    {grupo.nombre}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {grupo.items.map((item) => renderFilaItem(item))}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ mb: 2 }}>
              {itemsEditables.map((item) => renderFilaItem(item))}
            </Box>
          )}

          {itemsNuevos.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Servicios nuevos por agregar
              </Typography>
              <Stack spacing={1}>
                {itemsNuevos.map((item, index) => (
                  <Stack
                    key={`nuevo-${index}`}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: BRAND.color.primary,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.nombreServicio}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.refCliente} ·{" "}
                        {formatValorItem(
                          item.tipoMoneda,
                          item.valorConDescuento,
                        )}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => quitarNuevoServicio(index)}
                      disabled={procesando}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {mostrarFormNuevo ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Nuevo servicio
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={nuevoServicio.tipoItem}
                      onChange={(e) =>
                        actualizarNuevoServicio("tipoItem", e.target.value)
                      }
                    >
                      <MenuItem value="TAREA">TAREA</MenuItem>
                      <MenuItem value="PLAN">PLAN</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Empresa</InputLabel>
                    <Select
                      label="Empresa"
                      value={nuevoServicio.refCliente}
                      onChange={(e) =>
                        actualizarNuevoServicio("refCliente", e.target.value)
                      }
                    >
                      {empresasDisponibles.map((empresa) => (
                        <MenuItem key={empresa.rut} value={empresa.rut}>
                          {empresa.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      label="Moneda"
                      value={nuevoServicio.tipoMoneda}
                      onChange={(e) =>
                        actualizarNuevoServicio("tipoMoneda", e.target.value)
                      }
                    >
                      {MONEDAS.map((moneda) => (
                        <MenuItem key={moneda} value={moneda}>
                          {moneda}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {nuevoServicio.tipoItem === "PLAN" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Plan del catálogo</InputLabel>
                        <Select
                          label="Plan del catálogo"
                          value={nuevoServicio.refTipoPlan}
                          onChange={(e) =>
                            actualizarNuevoServicio(
                              "refTipoPlan",
                              e.target.value,
                            )
                          }
                        >
                          {planesActivos.map((plan) => (
                            <MenuItem key={plan.nombre} value={plan.nombre}>
                              {plan.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Frecuencia</InputLabel>
                        <Select
                          label="Frecuencia"
                          value={nuevoServicio.tipoFrecuencia}
                          onChange={(e) =>
                            actualizarNuevoServicio(
                              "tipoFrecuencia",
                              e.target.value,
                            )
                          }
                        >
                          {FRECUENCIAS_PLAN.map((frecuencia) => (
                            <MenuItem key={frecuencia} value={frecuencia}>
                              {frecuencia === "MES" ? "Mensual" : "Anual"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label="Siguiente ciclo"
                        value={strToDate(nuevoServicio.fechaSiguienteCiclo)}
                        onChange={(date) =>
                          actualizarNuevoServicio(
                            "fechaSiguienteCiclo",
                            dateToStr(date),
                          )
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth size="small" />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {nuevoServicio.tipoItem === "TAREA" && (
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha límite"
                      value={strToDate(nuevoServicio.fechaLimite)}
                      onChange={(date) =>
                        actualizarNuevoServicio("fechaLimite", dateToStr(date))
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </Grid>
                )}
                {/* Solo tiene sentido pactar un vencimiento para un descuento
                  que existe -- sin descuento no hay nada que vencer. */}
                {Number(nuevoServicio.descuento) > 0 && (
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha término descuento"
                      value={strToDate(nuevoServicio.fechaTerminoDesc)}
                      onChange={(date) =>
                        actualizarNuevoServicio(
                          "fechaTerminoDesc",
                          dateToStr(date),
                        )
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth size="small" />
                      )}
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sm={nuevoServicio.tipoItem === "PLAN" ? 6 : 12}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Nombre del servicio"
                    value={nuevoServicio.nombreServicio}
                    onChange={(e) =>
                      actualizarNuevoServicio("nombreServicio", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    label="Valor original"
                    value={nuevoServicio.valorOriginal}
                    onChange={(e) =>
                      actualizarNuevoServicio("valorOriginal", e.target.value)
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    label="Descuento (%)"
                    value={nuevoServicio.descuento}
                    onChange={(e) =>
                      actualizarNuevoServicio("descuento", e.target.value)
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Valor final"
                    value={formatValorItem(
                      nuevoServicio.tipoMoneda,
                      calcValorConDescuento(
                        nuevoServicio.valorOriginal,
                        nuevoServicio.descuento,
                      ),
                    )}
                    InputProps={{ readOnly: true }}
                    helperText="Calculado automáticamente"
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "grey.50" } }}
                  />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" onClick={agregarNuevoServicio}>
                  Agregar a la lista
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setMostrarFormNuevo(false);
                    setNuevoServicio(NUEVO_SERVICIO_INICIAL);
                  }}
                >
                  Cancelar
                </Button>
              </Stack>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mt: 1 }}
              >
                El resto de los detalles (objetivo, actividades, entregable) se
                completa editando el servicio una vez guardado.
              </Typography>
            </Paper>
          ) : (
            <Button
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={() => setMostrarFormNuevo(true)}
              disabled={procesando || empresasDisponibles.length === 0}
              sx={{ mb: 2 }}
            >
              Agregar servicio nuevo
            </Button>
          )}

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Lo que el cliente pidió y todavía no existe
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 1, display: "block" }}
            >
              Queda registrado en la bitácora de la negociación. No crea ningún
              servicio.
            </Typography>
            <Stack spacing={1}>
              {serviciosSolicitados.map((descripcion, index) => (
                <Stack
                  key={`solicitud-${index}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    placeholder="Ej: Pidió que le preparemos también la declaración de renta..."
                    value={descripcion}
                    onChange={(e) =>
                      actualizarLineaSolicitud(index, e.target.value)
                    }
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${descripcion.length}/1000 caracteres`}
                    disabled={procesando}
                  />
                  <IconButton
                    size="small"
                    onClick={() => quitarLineaSolicitud(index)}
                    disabled={procesando}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={agregarLineaSolicitud}
              disabled={procesando}
              sx={{ mt: 1 }}
            >
              Agregar solicitud
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={handleClose} disabled={procesando}>
            Cerrar
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={procesando || guardando}
            disabled={procesando || guardando || !hayCambiosPendientes}
            onClick={handleGuardar}
          >
            Guardar ajustes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

ModalAjusteServiciosV2.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  propuesta: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onAjusteGuardado: PropTypes.func,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

ModalAjusteServiciosV2.defaultProps = {
  onSuccess: undefined,
  onAjusteGuardado: undefined,
  maxWidth: "lg",
  fullWidth: true,
};
